import { useMemo } from "react";

import {
  useAdmAreaQuery,
  useGetAdminAreaHierarchy
} from "@/api/adminArea/userAdminAreaAPI";
import { useCountryQuery, useGetCountries } from "@/api/country/useCountryAPI";
import { MakerIcon } from "@/assets/icons";
import Dropdown from "@/components/ui/dropdown";
import { IOptionItem } from "@/components/ui/dropdown/types";
import { COUNTRY_NAME } from "@/constants/dataset";
import { capitalizeFirst } from "@/helpers/string";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { AdministrativeArea } from "@/types/adminArea";
import { CountryCode } from "@/types/common";
import { AdminAreaSettings, EAdminAreaLevel } from "@/types/spatial";

import { twMerge } from "tailwind-merge";

import { AdminAreaTreeSelection } from "../adminAreaTreeSelection";
import LoadingWrapper from "../ui/LoadingWrapper";

export interface AdminAreaSelectionProps {
  selectedData: AdminAreaSettings | null;
  disabled?: boolean;
  onSelectData: (area: AdminAreaSettings | null) => void;
  options?: AdminAreaOptions;
  treeConfig?: {
    enabled: boolean;
    selectedIds: string[];
    setSelectedIds: (ids: string[]) => void;
    className?: string;
  };
}

interface AdminAreaOptions {
  showCountryLevel?: boolean;
  prefixLabel?: string;
  showLabelAboveInput?: boolean;
  showLabel?: boolean;
  required?: LevelConfig;
  visible?: LevelConfig | null;
  showTitle?: boolean;
}

interface LevelConfig {
  lv0?: boolean;
  lv1?: boolean;
  lv2?: boolean;
  lv3?: boolean;
  lv4?: boolean;
}

interface AdminLevel {
  level: EAdminAreaLevel;
  value: IOptionItem | null | undefined;
  data: IOptionItem[];
  isLoading: boolean;
  placeholder: string;
  label: string;
  isRequired: boolean;
  isVisible: boolean;
}

const DEFAULT_OPTIONS: Required<AdminAreaOptions> = {
  showCountryLevel: true,
  showLabelAboveInput: false,
  prefixLabel: "",
  showLabel: false,
  required: { lv0: true, lv1: true, lv2: true, lv3: true, lv4: true },
  visible: null,
  showTitle: true
} as const;

const mapToOptions = (data: AdministrativeArea[]): IOptionItem[] =>
  data.map((item) => ({
    label: item.name,
    value: item.id,
    code: item.full_code,
    adm_unit: item.adm_unit
  }));

const getAdminUnitName = (data: AdministrativeArea[] | undefined): string =>
  capitalizeFirst(data?.[0]?.adm_unit ?? "");

const useCountryOptions = () => {
  const { data: countryData, isLoading: isLoadingCountryData } =
    useGetCountries();

  const countryOptions = useMemo(
    () =>
      countryData
        ?.filter((country) => country.name !== COUNTRY_NAME.global)
        .map((country) => ({ label: country.name, value: country.id })) || [],
    [countryData]
  );

  return { countryOptions, isLoadingCountryData };
};

const useAdminAreaData = (selectedData: AdminAreaSettings | null) => {
  // TODO: need to refactor further when we support more than level 3
  const { data: level0Data, isLoading: isGettingLv0Data } = useCountryQuery(
    selectedData?.lv0
  );
  const { data: level1Data, isLoading: isGettingLv1Data } = useAdmAreaQuery(
    selectedData,
    1
  );
  const { data: level2Data, isLoading: isGettingLv2Data } = useAdmAreaQuery(
    selectedData,
    2
  );
  const { data: level3Data, isLoading: isGettingLv3Data } = useAdmAreaQuery(
    selectedData,
    3
  );

  const levelOptions = useMemo(
    () => ({
      lv1: mapToOptions(level0Data?.data ?? []),
      lv2: mapToOptions(level1Data?.data ?? []),
      lv3: mapToOptions(level2Data?.data ?? []),
      lv4: mapToOptions(level3Data?.data ?? [])
    }),
    [level0Data, level1Data, level2Data, level3Data]
  );

  return {
    levelData: { level0Data, level1Data, level2Data, level3Data },
    levelOptions,
    isLoading: {
      lv0: isGettingLv0Data,
      lv1: isGettingLv1Data,
      lv2: isGettingLv2Data,
      lv3: isGettingLv3Data
    }
  };
};

const NoCountrySelected = () => (
  <div className="flex flex-col items-center justify-center rounded-md py-3 px-4 mt-3">
    <MakerIcon className="fill-light-gray" width={21} height={26} />
    <p className="text-center font-sans text-txtPlaceholder">
      Please select a country to select its administrative area levels.
    </p>
  </div>
);

const TitleAdministrativeAreaSelection = ({
  required
}: {
  required: boolean;
}) => (
  <div
    className={twMerge(
      "text-txtPlaceholder font-bold",
      required && "required mt-3"
    )}
  >
    Administrative Area Selection
  </div>
);

const CountryDropdown = ({
  selectedData,
  onSelectData,
  countryOptions,
  isLoadingCountryData,
  config
}: {
  selectedData: AdminAreaSettings | null;
  onSelectData: (area: AdminAreaSettings | null) => void;
  countryOptions: { label: string; value: CountryCode }[];
  isLoadingCountryData: boolean;
  config: Required<AdminAreaOptions>;
}) => {
  const { isGlobalFilterEnabled } = useAppFeatures();
  const handleSelectCountry = (data: IOptionItem | null) => {
    onSelectData({
      lv0: data,
      lv1: undefined,
      lv2: undefined,
      lv3: undefined,
      lv4: undefined
    });
  };

  let countryLabel = "";
  if (!config.showLabel) {
    countryLabel = "Country";
    if (config.required.lv0) {
      countryLabel += "*";
    }
  }

  return (
    <div>
      <Dropdown
        value={selectedData?.lv0}
        className="mt-2"
        placeholder={`${isGlobalFilterEnabled ? "" : "Select"} Country`}
        textTransform="capitalize-words"
        data={countryOptions}
        name="admin-area-level-0-country"
        withSearch
        showLabelOnTop={config.showLabelAboveInput}
        onSelect={handleSelectCountry}
        label={countryLabel}
        isLoading={isLoadingCountryData}
      />
    </div>
  );
};

export const AdminAreaSelection = ({
  selectedData,
  onSelectData,
  options = {},
  treeConfig,
  disabled
}: AdminAreaSelectionProps) => {
  const config = { ...DEFAULT_OPTIONS, ...options };
  const {
    showLabelAboveInput,
    showCountryLevel,
    showLabel,
    prefixLabel,
    required,
    visible,
    showTitle
  } = config;

  const { countryOptions, isLoadingCountryData } = useCountryOptions();
  const { levelData, levelOptions, isLoading } = useAdminAreaData(selectedData);

  const {
    data: adminAreaHierarchyData,
    isLoading: isLoadingAdminAreaHierarchy
  } = useGetAdminAreaHierarchy(selectedData?.lv0?.value ?? "", {
    enabled: !!selectedData?.lv0?.value && !!treeConfig?.enabled
  });

  const handleSelectAdminArea = (
    selectedAdminArea: IOptionItem | null,
    level: EAdminAreaLevel
  ) => {
    const levelMap: Record<EAdminAreaLevel, Partial<AdminAreaSettings>> = {
      [EAdminAreaLevel.LV0]: {
        lv0: selectedAdminArea,
        lv1: undefined,
        lv2: undefined,
        lv3: undefined,
        lv4: undefined
      },
      [EAdminAreaLevel.LV1]: {
        ...selectedData,
        lv1: selectedAdminArea,
        lv2: undefined,
        lv3: undefined,
        lv4: undefined
      },
      [EAdminAreaLevel.LV2]: {
        ...selectedData,
        lv2: selectedAdminArea,
        lv3: undefined,
        lv4: undefined
      },
      [EAdminAreaLevel.LV3]: {
        ...selectedData,
        lv3: selectedAdminArea,
        lv4: undefined
      },
      [EAdminAreaLevel.LV4]: {
        ...selectedData,
        lv4: selectedAdminArea
      }
    };
    onSelectData(levelMap[level]);
  };

  const adminLevels: AdminLevel[] = useMemo(
    () => [
      {
        level: EAdminAreaLevel.LV0,
        value: selectedData?.lv0,
        data: countryOptions,
        isLoading: isLoading.lv0,
        placeholder: `${prefixLabel} Country`,
        label: `Country ${required.lv0 && !isLoading.lv0 && !!countryOptions.length ? "*" : ""}`,
        isRequired: !!required.lv0,
        isVisible: !visible ? !!countryOptions.length : !!visible.lv0
      },
      {
        level: EAdminAreaLevel.LV1,
        value: selectedData?.lv1,
        data: levelOptions.lv1,
        isLoading: isLoading.lv0,
        placeholder: `${prefixLabel} ${getAdminUnitName(levelData.level0Data?.data)}`,
        label: `${getAdminUnitName(levelData.level0Data?.data)} ${
          required.lv1 && !isLoading.lv0 && !!levelOptions.lv1.length ? "*" : ""
        }`,
        isRequired: !!required.lv1,
        isVisible: !visible ? !!levelOptions.lv1.length : !!visible.lv1
      },
      {
        level: EAdminAreaLevel.LV2,
        value: selectedData?.lv2,
        data: levelOptions.lv2,
        isLoading: isLoading.lv1,
        placeholder: `${prefixLabel} ${getAdminUnitName(levelData.level1Data?.data)}`,
        label: `${getAdminUnitName(levelData.level1Data?.data)} ${
          required.lv2 && !isLoading.lv1 && !!levelOptions.lv2.length ? "*" : ""
        }`,
        isRequired: !!required.lv2,
        isVisible: !visible ? !!levelOptions.lv2.length : !!visible.lv2
      },
      {
        level: EAdminAreaLevel.LV3,
        value: selectedData?.lv3,
        data: levelOptions.lv3,
        isLoading: isLoading.lv2,
        placeholder: `${prefixLabel} ${getAdminUnitName(levelData.level2Data?.data)}`,
        label: `${getAdminUnitName(levelData.level2Data?.data)} ${
          required.lv3 && !isLoading.lv2 && !!levelOptions.lv3.length ? "*" : ""
        }`,
        isRequired: !!required.lv3,
        isVisible: !visible ? !!levelOptions.lv3.length : !!visible.lv3
      },
      {
        level: EAdminAreaLevel.LV4,
        value: selectedData?.lv4,
        data: levelOptions.lv4,
        isLoading: isLoading.lv3,
        placeholder: `${prefixLabel} ${getAdminUnitName(levelData.level3Data?.data)}`,
        label: `${getAdminUnitName(levelData.level3Data?.data)} ${
          required.lv4 && !isLoading.lv3 && !!levelOptions.lv4.length ? "*" : ""
        }`,
        isRequired: !!required.lv4,
        isVisible: !visible ? !!levelOptions.lv4.length : !!visible.lv4
      }
    ],
    [
      selectedData,
      countryOptions,
      levelOptions,
      isLoading,
      levelData,
      required,
      visible,
      prefixLabel
    ]
  );

  const adminAreaLevels = useMemo(() => {
    return adminLevels.map((level) => {
      const { value, data } = level;
      if (value && (!value.label || !value.adm_unit)) {
        const matchingOption = data.find(
          (option) => option.value === value.value
        );

        if (matchingOption) {
          return {
            ...level,
            value: {
              ...value,
              label: matchingOption.label || value.label,
              adm_unit: matchingOption.adm_unit || value.adm_unit
            }
          };
        }
      }

      return level;
    });
  }, [adminLevels]);

  const shouldShowLevel = (levelIndex: number): boolean => {
    if (levelIndex === 0) return true;
    if (levelIndex === 1) return !!selectedData?.lv1;
    if (levelIndex === 2) return !!selectedData?.lv2;
    if (levelIndex === 3) return !!selectedData?.lv3;
    return false;
  };

  return (
    <div
      className={twMerge(
        "flex text-xs flex-col",
        treeConfig?.enabled && "flex-1 min-h-0"
      )}
    >
      {showCountryLevel && (
        <CountryDropdown
          selectedData={selectedData}
          onSelectData={onSelectData}
          countryOptions={countryOptions}
          isLoadingCountryData={isLoadingCountryData}
          config={config}
        />
      )}

      {showTitle && (
        <TitleAdministrativeAreaSelection required={showCountryLevel} />
      )}

      {!selectedData?.lv0 && <NoCountrySelected />}
      {!treeConfig?.enabled &&
        selectedData?.lv0 &&
        // Skip the first element (level 0) as it represents the country level
        adminAreaLevels.slice(1).map((adminLevel, index) => {
          return (
            <LoadingWrapper
              loadingClassName="h-[50px]"
              isLoading={adminLevel.isLoading}
              key={adminLevel.level}
            >
              {shouldShowLevel(index) &&
                adminLevel.data?.length > 0 &&
                adminLevel.isVisible && (
                  <Dropdown
                    disabled={disabled}
                    key={adminLevel.level}
                    value={adminLevel.value}
                    name={`admin-area-${adminLevel.level}`}
                    className="mt-3"
                    menuListClassName="max-h-[200px]"
                    placeholder={adminLevel.placeholder}
                    showLabelOnTop={showLabelAboveInput}
                    labelBorder={adminLevel.label}
                    data={adminLevel.data}
                    textTransform="capitalize-words"
                    isLoading={adminLevel.isLoading}
                    label={showLabel ? adminLevel.label : ""}
                    withSearch
                    visible={adminLevel.isVisible}
                    onSelect={(data) =>
                      handleSelectAdminArea(data, adminLevel.level)
                    }
                  />
                )}
            </LoadingWrapper>
          );
        })}

      {treeConfig?.enabled && selectedData?.lv0 && (
        <LoadingWrapper isLoading={isLoadingAdminAreaHierarchy}>
          <AdminAreaTreeSelection
            hierarchyData={adminAreaHierarchyData?.data}
            selectedAreaIds={treeConfig.selectedIds}
            setSelectedIds={treeConfig.setSelectedIds}
            className={treeConfig.className}
          />
        </LoadingWrapper>
      )}
    </div>
  );
};

export default AdminAreaSelection;
